import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { navLinks, contactInfo } from "../mock";
import { goToCheckout } from "../lib/checkout";

const LOGO = "/logo.webp";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, setOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur shadow-[0_2px_20px_-8px_rgba(22,54,92,0.25)]"
          : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[76px]">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={LOGO} alt="CDL Academy" className="h-16 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 font-head font-semibold text-sm tracking-wide uppercase transition-colors ${
                    active ? "text-brand" : "text-slate-600 hover:text-brand"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${contactInfo.phone.replace(/[^0-9]/g, "")}`}
              className="flex items-center gap-2 text-slate-700 hover:text-brand transition-colors font-head font-semibold text-sm"
            >
              <Phone className="w-4 h-4 text-steel" />
              {contactInfo.phone}
            </a>
            <a
              href="https://app.cdlacademy.com/login?returnUrl=%2FCourseInfo"
              className="btn-brand rounded-md px-5 py-2.5 text-sm inline-flex items-center justify-center"
            >
              Returning Students
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden text-brand p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-5 py-6 space-y-1 shadow-lg">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="block px-3 py-3 font-head font-semibold uppercase tracking-wide text-slate-700 hover:text-brand border-b border-slate-100"
            >
              {link.label}
            </Link>
          ))}
          <a
            href="https://app.cdlacademy.com/login?returnUrl=%2FCourseInfo"
            className="btn-brand rounded-md w-full py-3 mt-4 inline-flex items-center justify-center"
          >
            Returning Students
          </a>
          <a
            href={`tel:${contactInfo.phone.replace(/[^0-9]/g, "")}`}
            className="flex items-center justify-center gap-2 text-slate-600 pt-4 font-head font-semibold"
          >
            <Phone className="w-4 h-4 text-steel" /> {contactInfo.phone}
          </a>
        </div>
      )}
    </header>
  );
}
